import { ShipmentLocationActionRequestDtoLocationActionType as LocationActionType } from '@uturn/api/v1';
import { z } from '@uturn/portal/zod';
import { formValidationMessage } from '@uturn/portal/zod/utils';
import { shippingLineSchema } from '../container';

/**
 * a.k.a. locationActions
 *
 * ShipmentLocationActionRequestDto as LocationAction, // [POST] Shipment
 * ShipmentLocationActionDto, //                          [GET]  Shipment
 * ShipmentLocationActionUpdateDto, //                    [PUT]  Shipment
 */

/**
 * Field max lengths — single source of truth, mirrored from the API (ShipmentLocationActionRequestDto). 
 */
export const REFERENCE_MAX_LENGTH = 50;
export const VESSEL_NAME_MAX_LENGTH = 50;
export const PORT_MAX_LENGTH = 30;

/** ShipmentLocationActionRequestDtoLocationActionType */
const locationActionTypeSchema = z.nativeEnum(LocationActionType).optional();

/** AddressDto */
const addressSchema = z.object({
	city: z.string().optional(),
	countryCode: z.string().optional(),
	houseNumber: z.string().optional(),
	latitude: z.number().optional(),
	longitude: z.number().optional(),
	postalCode: z.string().optional(),
	street: z.string().optional(),
});

/** ShippingLocationRequestDto for POST and PUT Shipment */
export const locationRequestSchema = z.object({
	id: z.number().optional(),
	googlePlaceId: z.string().optional(), // required when GET Shipment
});

/** ShippingLocationDto for GET Shipment */
const locationResponseSchema = z.object({
	name: z.string().optional(), // required when GET Shipment
	address: addressSchema.optional(),
	averageRating: z.number().optional(),
	fourShippingTerminalCode: z.string().optional(),
	portbaseBics: z.string().optional(),
	timesRated: z.number().optional(),
});

export const locationSchema = locationRequestSchema.merge(
	locationResponseSchema,
);

/** ShipmentLocationActionRequestDto for POST Shipment */
const locationActionRequestSchema = z.object({
	// .optional() allows val to be undefined so the refine below can catch it
	// and show a user-friendly error. Without it, Zod throws its own "Required"
	// error before the refine runs, bypassing our custom message.
	location: locationSchema.optional().refine(
		(val) =>
			val !== undefined &&
			(val.id !== undefined || val.googlePlaceId !== undefined),
		formValidationMessage('location_select_from_suggestions'),
	),
	locationActionType: locationActionTypeSchema, // 🔴 Required
	// .refine((val) => !!val, {
	// 	message: 'Action type is required', // Not on the form, so, useless validation?
	// }),
	customerName: z.string().optional(),
	reference: z.string().max(REFERENCE_MAX_LENGTH).optional(),
	remarks: z.string().optional(),
	shippingLineId: z.coerce.number().optional(), // from the new shipment only
	sequenceNumber: z.number().optional(), // required if creating or publishing
	port: z.string().max(PORT_MAX_LENGTH).optional(),
	vesselName: z.string().max(VESSEL_NAME_MAX_LENGTH).optional(),
});

const locationActionRequestConceptSchema = locationActionRequestSchema.extend({
	dateFrom: z.string().datetime({ offset: true }).optional(),
	dateUntil: z.string().datetime({ offset: true }).optional(),
});

const locationActionRequestPublishSchema = locationActionRequestSchema.extend({
	// LEGACY: z.string().pipe(z.coerce.date()),
	// .transform((value) => value && convertToIsoDateWithTimezone(new Date(value))),
	dateFrom: z.string().datetime({ offset: true }), // 🔴 Required
	dateUntil: z.string().datetime({ offset: true }), // 🔴 Required
});

/** ShipmentLocationActionDto for GET Shipment */
const locationActionResponseSchema = z.object({
	/**
	 * number <= BE
	 * string <= FE: '1' | '2' | '3' | crypto.randomUUID()
	 */
	id: z.union([z.string(), z.number()]).optional(),
	shippingLine: shippingLineSchema.optional(),
	eta: z.string().optional(),
	arrival: z.string().optional(),
	departure: z.string().optional(),
});

export const locationActionConceptSchema =
	locationActionRequestConceptSchema.merge(locationActionResponseSchema);

export const locationActionPublishSchema =
	locationActionRequestPublishSchema.merge(locationActionResponseSchema);
